<template>
  <div :id="`announcement-${id}`" :class="['announcement', { collapsed: localCollapsed }]">
    <div class="header" @click="toggleCollapse">
      <h3 class="title">{{ title }}</h3>
      <div class="toggle-icon">
        <span v-if="localCollapsed">▼</span>
        <span v-else>▲</span>
      </div>
    </div>
    <div v-if="!localCollapsed" class="content">
      <SanitizedHtml :html="message" class="message" />
      <div v-if="additionalInfo" class="additional-info">
        <div class="period-info">{{ additionalInfo }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits, ref, onMounted, watch } from 'vue';
import SanitizedHtml from '../SanitizedHtml.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  message: {
    type: String,
    required: true,
  },
  additionalInfo: {
    type: String,
    default: '',
  },
  additionalInfoSchedule: {
    type: String,
    default: '',
  },
  id: {
    type: [String, Number],
    required: true,
  },
  collapsed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggle']);

const localCollapsed = ref(props.collapsed);

// クッキーを取得する関数
const getCookie = (name: string): string | null => {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=');
    if (cookieName === name) {
      return cookieValue;
    }
  }
  return null;
};

// クッキーを設定する関数
const setCookie = (name: string, value: string): void => {
  // 翌日の0時に期限が切れるように設定
  const tomorrow = new Date();
  tomorrow.setHours(24, 0, 0, 0); // 翌日の0時に設定
  const expires = `expires=${tomorrow.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/`;
  console.log(`Cookie set: ${name}=${value}`);
};

// お知らせの状態を保存
const saveAnnouncementState = (id: string | number, isCollapsed: boolean): void => {
  setCookie(`${id}_state`, isCollapsed ? 'collapsed' : 'open');
};

const toggleCollapse = () => {
  localCollapsed.value = !localCollapsed.value;
  saveAnnouncementState(props.id, localCollapsed.value);
  emit('toggle', { isCollapsed: localCollapsed.value });
};

// コンポーネントマウント時にクッキーから状態を読み込む
onMounted(() => {
  const cookieState = getCookie(`${props.id}_state`);
  // Removed unnecessary console.log statement

  if (cookieState === 'collapsed') {
    localCollapsed.value = true;
  } else if (cookieState === 'open') {
    localCollapsed.value = false;
  }

  // 初期状態を親コンポーネントに通知
  emit('toggle', { isCollapsed: localCollapsed.value });
});

// propsの変更を監視
watch(
  () => props.collapsed,
  (newValue) => {
    if (newValue !== localCollapsed.value) {
      localCollapsed.value = newValue;
    }
  }
);
</script>

<style scoped>
.announcement {
  border: none;
  border-radius: 0;
  margin-bottom: 8px;
  background-color: #e5f4fa;
  overflow: hidden;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
  background-color: #d1eaf5;
  border-bottom: none;
}

.header:hover {
  background-color: #d1eaf5;
}

/* メンテナンスヘッダーのスタイル */
.announcement[id='announcement-maintenance'] .header,
.announcement[id='announcement-general'] .header {
  background-color: #d1eaf5;
  color: #231815;
  transition: background-color 0.3s ease;
}

.announcement[id='announcement-maintenance'] .header:hover,
.announcement[id='announcement-general'] .header:hover {
  background-color: #c0e0f0;
}

.title {
  margin: 0;
  font-size: 14px;
  font-weight: bold;
  text-align: center;
  width: 100%;
  color: #231815;
}

.toggle-icon {
  font-size: 14px;
  color: #666;
}

.content {
  max-width: 840px;
  margin: 0 auto;
  padding: 10px 15px;
  background-color: #e5f4fa;
  transition: 0.3s ease;
  display: flex;
  justify-content: center;
}

.message {
  font-size: 14px;
  white-space: pre-line;
  line-height: 1.8;
  text-align: justify;
  margin-bottom: 10px;
}

.additional-info {
  padding: 0;
}

.period-info {
  font-size: 13px;
}
</style>
